import React, { useEffect, useState } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
	Paper,
	Box,
	Typography,
	useTheme,
	IconButton,
	CircularProgress,
} from "@mui/material";
import {
	ExpandLess,
	ExpandMore,
	MedicalServices,
	MonetizationOn,
	NoMeetingRoom,
	Person,
	School,
} from "@mui/icons-material";
import moment from "moment";
import employeeSchema from "./schema";
import {
	generalInfoFields,
	medicalFields,
	payrollFields,
	educationFields,
	separationFields,
} from "./fieldConfigs";
import { useEmployeeData } from "./useEmployeeData";
import { updateEmployee } from "../../../reducers/EmployeeReducer";
import PATCloseButton from "../../../components/Buttons/PATCloseButton";
import PATSaveButton from "../../../components/Buttons/PATSaveButton";
import EmergencyContactsTable from "./EmergencyContactsTable";
import PhotosTable from "../../../components/PerPhotos/PhotoTable";
import FormSection from "../../../components/Form/FormSection";
import { PATH_EMPLOYEE } from "../../../utils/routeConstants";
import {
	AMSReportID,
	entityNamesForCustomId,
	serviceNames,
} from "../../../utils/constants";
import { getService } from "../../../reducers/service";
import { updateTrainingParticipant } from "../../../reducers/TrainingParticipantReducer";
import { updateTrainingCertification } from "../../../reducers/TrainingCertificationReducer";
import PerNotesTable from "../../../components/PerNotes/PerNotesTable";
import {
	perNotesOptions,
	PerPhotoType,
	PerTagType,
} from "../../../utils/enums";
import TagsTable from "../../../components/Tags/TagsTable";
import AssignmentHistoryTable from "./AssignmentHistoryTable";
import GeneralInfoSection from "./GeneralInfoSection";
import {
	areAllPermissionsDisabled,
	formatModulePermission,
	getAccessPermission,
	getModuleAccessPermission,
} from "../../../reducers/PermissionsReducer";
import { notify } from "../../../reducers/NotifierReducer";
import PATPrintButton from "../../../components/Buttons/PATPrintButton";
import PrintBase64Pdf from "../../../components/PrintBase64Pdf";
import { printAMSReport } from "../../../utils/functions";

const EmployeeEditForm = () => {
	const { id } = useParams();
	const dispatch = useDispatch();
	const history = useHistory();
	const theme = useTheme();
	const location = useLocation();
	const queryParams = new URLSearchParams(location.search);
	const readOnly = queryParams.get("readOnly") === "true";

	useEffect(() => {
		if (location.search && queryParams.get("readOnly") !== "true") {
			dispatch(notify("The requested page does not exist.", "warning"));
			history.push(PATH_EMPLOYEE);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id, location.search, history]);

	const [allExpanded, setAllExpanded] = useState(false);
	const [latestAssignment, setLatestAssignment] = useState({});
	const [printReport, setPrintReport] = useState(false);
	const [reportString, setReportString] = useState("");

	const personnelTags = useSelector((state) =>
		state.personnelTags.personnelTags &&
		state.personnelTags.personnelTags[PerTagType.Employee]
			? state.personnelTags.personnelTags[PerTagType.Employee][id]
			: []
	);

	const personnelEmergencyContacts = useSelector(
		(state) =>
			state.personnelEmergencyContacts.personnelEmergencyContacts[id] ||
			[]
	);

	const personnelAssignmentHistory = useSelector(
		(state) =>
			state.personnelAssignmentHistory.personnelAssignmentHistory[id] ||
			[]
	);

	const personnelPhotos = useSelector(
		(state) =>
			state.personnelPhotos.photosByEntity?.[PerPhotoType.Employee]?.[
				id
			] ?? []
	);

	const isLoadingPhotos = useSelector(
		(state) => state.personnelPhotos.loading
	);

	const thumbnailPhoto = personnelPhotos.find((photo) => photo.isThumbnail);

	const perNotes = useSelector((state) =>
		state.notes.notes && state.notes.notes["Employees"]
			? state.notes.notes["Employees"][id]
			: []
	);

	const employeePermissions = getModuleAccessPermission("employees", "any");
	const employeeModulePermissions = formatModulePermission(
		"EmplopyeeModule",
		employeePermissions,
		["Employee Record"]
	);
	const isDisabledModule = areAllPermissionsDisabled(
		employeeModulePermissions["EmplopyeeModule"]
	);

	const assignmentHistoryPermission = getAccessPermission(
		"employees",
		"Assignment History",
		"any"
	);
	const assignmentHistoryReadPermission =
		assignmentHistoryPermission?.Read ?? true;
	const assignmentHistoryAddPermission =
		assignmentHistoryPermission?.Create ?? true;
	const assignmentHistoryEditPermission =
		assignmentHistoryPermission?.Edit ?? true;
	const assignmentHistoryDeletePermission =
		assignmentHistoryPermission?.Delete ?? true;

	const educationPermission = getAccessPermission(
		"employees",
		"Education",
		"any"
	);
	const educationReadPermission = educationPermission?.Read ?? true;
	const educationEditPermission = educationPermission?.Edit ?? true;

	const separationPermission = getAccessPermission(
		"employees",
		"Separation",
		"any"
	);
	const separationReadPermission = separationPermission?.Read ?? true;
	const separationEditPermission = separationPermission?.Edit ?? true;

	const tagPermission = getAccessPermission("employees", "Tags", "any");
	const tagReadPermission = tagPermission?.Read ?? true;

	const notePermission = getAccessPermission("employees", "Notes", "any");
	const noteReadPermission = notePermission?.Read ?? true;

	const photoPermission = getAccessPermission("employees", "Photos", "any");
	const photoReadPermission = photoPermission?.Read ?? true;

	const payrollPermission = getAccessPermission(
		"employees",
		"Payroll",
		"any"
	);
	const payrollReadPermission = payrollPermission?.Read ?? true;
	const payrollEditPermission = payrollPermission?.Edit ?? true;

	const medicalPermission = getAccessPermission(
		"employees",
		"Medical Fields",
		"any"
	);
	const medicalReadPermission = medicalPermission?.Read ?? true;
	const medicalEditPermission = medicalPermission?.Edit ?? true;

	const emergencyPermission = getAccessPermission(
		"employees",
		"Emergency Contact",
		"any"
	);
	const emergencyReadPermission = emergencyPermission?.Read ?? true;
	const emergencyAddPermission = emergencyPermission?.Create ?? true;
	const emergencyEditPermission = emergencyPermission?.Edit ?? true;
	const emergencyDeletePermission = emergencyPermission?.Delete ?? true;

	const generalInfoPermission = getAccessPermission(
		"employees",
		"General Info",
		"any"
	);
	const generalInfoReadPermission = generalInfoPermission?.Read ?? true;
	const generalInfoeEditPermission = generalInfoPermission?.Edit ?? true;

	let thumbnailSrc = null;
	if (thumbnailPhoto?.imageData) {
		const base64 =
			typeof thumbnailPhoto.imageData === "string"
				? thumbnailPhoto.imageData
				: btoa(
						new Uint8Array(
							thumbnailPhoto.imageData?.data ||
								thumbnailPhoto.imageData
						).reduce(
							(data, byte) => data + String.fromCharCode(byte),
							""
						)
				  );

		thumbnailSrc = `data:image/jpeg;base64,${base64}`;
	}

	const handleToggleAll = () => {
		setAllExpanded((prev) => !prev);
	};

	const {
		control,
		handleSubmit,
		reset,
		getValues,
		setValue,
		watch,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(employeeSchema),
		defaultValues: {
			id: null,
			firstName: "",
			lastName: "",
			middleName: "",
			suffix: "",
			dob: "",
			ssn: "",
			email: "",
			gender: "",
			employeeNum: "",
			phoneNo: "",
			cellNo: "",
			workPhoneNum: "",
			pagerNo: "",
			dl: "",
			dlState: "",
			dlExpire: "",
			race: "",
			status: "",
			address: "",
			rCity: "",
			rState: "",
			rZip: "",
			mAddress: "",
			mCity: "",
			mState: "",
			mZip: "",
			maritalStatus: "",
			spouseName: "",
			spouseSSN: "",
			spouseEmployer: "",
			spousePhoneNumber: "",
			emergencyContact: "",
			ecHomePhone: "",
			ecCellNo: "",
			contactAddress: "",
			contactCity: "",
			contactState: "",
			contactZip: "",
			bloodType: "",
			medicalNotes: "",
			relationship: "",
			masterCostCenter: "",
			salary: "",
			annualSalary: "",
			titlePosition: "",
			rank: "",
			classCode: "",
			grade: "",
			personnelNum: "",
			personnelArea: "",
			subArea: "",
			orgUnit: "",
			contract: "",
			positionStartDate: "",
			positionEndDate: "",
			dateLwopMilitary: "",
			dateReturnedPayroll: "",
			perfRevDate: "",
			careerServiceDate: "",
			leaveAccDate: "",
			careerServicePayment: "",
			orderNum: "",
			percentage: "",
			internalCC: "",
			wbsElement: "",
			wbscc: "",
			otherCC: "",
			orgChart: false,
			flsaStatus: "",
			cgdg: false,
			clip: false,
			commissionedDate: "",
			trooperDate: "",
			latestHireDate: "",
			hireDate: "",
			badgeNum: "",
			department: "",
			departmentDate: "",
			section: "",
			sectionDate: "",
			residentialCounty: "",
			assignedCounty: "",
			education: "",
			notes: "",
			militaryNotes: "",
			dropDate: "",
			retireDate: "",
			separationDate: "",
			separationCode: "",
			separationComments: "",
			rehire: "",
			autoGenerateID: false,
		},
	});

	const {
		personName,
		genderOptions,
		raceOptions,
		employmentOptions,
		stateOptions,
		maritalOptions,
		bloodTypeOptions,
		flsaStatusOptions,
		tagTypeOptions,
		countyOptions,
		rankOptions,
		rehireOptions,
		titlePositionOptions,
		departmentOptions,
		sectionOptions,
		educationOptions,
		suffixOptions,
		cityOptions,
		oldEmployeeNum,
		latestAssignmentData,
		loaded,
		userAgency,
		user,
	} = useEmployeeData(id, reset);

	useEffect(() => {
		setLatestAssignment(latestAssignmentData);
	}, [latestAssignmentData]);

	const rCity = watch("rCity");
	const mCity = watch("mCity");

	useEffect(() => {
		const city = cityOptions.find((c) => c.value === rCity);
		if (city?.state) {
			setValue("rState", city.state);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rCity, cityOptions]);

	useEffect(() => {
		const city = cityOptions.find((c) => c.value === mCity);
		if (city?.state) {
			setValue("mState", city.state);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mCity, cityOptions]);

	const options = {
		gender: genderOptions,
		race: raceOptions,
		status: employmentOptions,
		rState: stateOptions,
		mState: stateOptions,
		maritalStatus: maritalOptions,
		bloodType: bloodTypeOptions,
		contactState: stateOptions,
		flsaStatus: flsaStatusOptions,
		residentialCounty: countyOptions,
		assignedCounty: countyOptions,
		rank: rankOptions,
		rehire: rehireOptions,
		titlePosition: titlePositionOptions,
		department: departmentOptions,
		section: sectionOptions,
		education: educationOptions,
		suffix: suffixOptions,
		rCity: cityOptions,
		mCity: cityOptions,
		dlState: stateOptions,
	};

	const generateCustomId = async () => {
		const data = { EntityName: entityNamesForCustomId.personnelEmployeeId };
		const service = getService(serviceNames.customId);
		service.timeout = 20000;

		const id = await service.create(data);
		return id;
	};

	const autoGenerateID = watch("autoGenerateID");

	useEffect(() => {
		const fetchAndSetId = async () => {
			if (autoGenerateID) {
				const customId = await generateCustomId();
				if (customId) {
					setValue("employeeNum", customId);
				}
			}
		};
		fetchAndSetId();
	}, [autoGenerateID, setValue]);

	const status = watch("status");
	const selectedOption = employmentOptions.find(
		(opt) => opt.value === status
	);
	const statusHighlight = selectedOption?.activeEmployee;

	const dlExpireValue = watch("dlExpire");
	const dateHighlight =
		dlExpireValue &&
		moment(dlExpireValue, "MM/DD/YYYY").isBefore(moment(), "day");

	const onSubmit = async (data) => {
		const cleanedData = {
			firstName: data.firstName.trim() || null,
			lastName: data.lastName.trim() || null,
			middleName: data.middleName.trim() || null,
			suffix: data.suffix.trim() || null,
			dob: data?.dob
				? moment(data.dob, "MM/DD/YYYY").startOf("day").format("YYYY-MM-DD HH:mm:ss.SSS")
				: null,
			ssn: data.ssn.trim() || null,
			email: data.email ? data.email.trim() : null,
			gender: data.gender || null,
			race: data.race || null,
			employeeNum: data.employeeNum.trim() || null,
			phoneNo: data.phoneNo.trim() || null,
			cellNo: data.cellNo.trim() || null,
			pagerNo: data.pagerNo.trim() || null,
			workPhoneNum: data.workPhoneNum.trim() || null,
			dl: data.dl.trim() || null,
			dlState: data.dlState || null,
			dlExpire: data.dlExpire
				? moment(data.dlExpire, "MM/DD/YYYY").format("YYYY-MM-DD")
				: null,
			status: data.status || null,
			address: data.address.trim() || null,
			rCity: data.rCity || null,
			rState: data.rState || null,
			rZip: data.rZip.trim() || null,
			mAddress: data.mAddress.trim() || null,
			mCity: data.mCity || null,
			mState: data.mState || null,
			mZip: data.mZip.trim() || null,
			maritalStatus: data.maritalStatus || null,
			spouseName: data.spouseName.trim() || null,
			spouseSSN: data.spouseSSN.trim() || null,
			spouseEmployer: data.spouseEmployer.trim() || null,
			spousePhoneNumber: data.spousePhoneNumber.trim() || null,
			emergencyContact: data.emergencyContact.trim() || null,
			ecHomePhone: data.ecHomePhone.trim() || null,
			ecCellNo: data.ecCellNo.trim() || null,
			contactAddress: data.contactAddress.trim() || null,
			contactCity: data.contactCity.trim() || null,
			contactState: data.contactState || null,
			contactZip: data.contactZip.trim() || null,
			medicalNotes: data.medicalNotes.trim() || null,
			bloodType: data.bloodType || null,
			relationship: data.relationship || null,
			masterCostCenter: data.masterCostCenter
				? data.masterCostCenter.trim()
				: null,
			salary: data.salary.trim() || null,
			annualSalary: data.annualSalary.trim() || null,
			titlePosition: data.titlePosition || null,
			rank: data.rank || null,
			classCode: data.classCode.trim() || null,
			grade: data.grade.trim() || null,
			personnelNum: data.personnelNum.trim() || null,
			personnelArea: data.personnelArea.trim() || null,
			subArea: data.subArea.trim() || null,
			orgUnit: data.orgUnit.trim() || null,
			contract: data.contract.trim() || null,
			positionStartDate: data.positionStartDate
				? moment(data.positionStartDate, "MM/DD/YYYY").format(
						"YYYY-MM-DD"
				  )
				: null,
			positionEndDate: data.positionEndDate
				? moment(data.positionEndDate, "MM/DD/YYYY").format(
						"YYYY-MM-DD"
				  )
				: null,
			dateLwopMilitary: data.dateLwopMilitary
				? moment(data.dateLwopMilitary, "MM/DD/YYYY").format(
						"YYYY-MM-DD"
				  )
				: null,
			dateReturnedPayroll: data.dateReturnedPayroll
				? moment(data.dateReturnedPayroll, "MM/DD/YYYY").format(
						"YYYY-MM-DD"
				  )
				: null,
			perfRevDate: data.perfRevDate
				? moment(data.perfRevDate, "MM/DD/YYYY").format("YYYY-MM-DD")
				: null,
			careerServiceDate: data.careerServiceDate
				? moment(data.careerServiceDate, "MM/DD/YYYY").format(
						"YYYY-MM-DD"
				  )
				: null,
			leaveAccDate: data.leaveAccDate
				? moment(data.leaveAccDate, "MM/DD/YYYY").format("YYYY-MM-DD")
				: null,
			careerServicePayment: data.careerServicePayment.trim() || null,
			orderNum: data.orderNum.trim() || null,
			percentage: data.percentage.trim() || null,
			internalCC: data.internalCC.trim() || null,
			wbsElement: data.wbsElement.trim() || null,
			wbscc: data.wbscc.trim() || null,
			otherCC: data.otherCC.trim() || null,
			orgChart: data.orgChart || false,
			flsaStatus:
				data.flsaStatus === "Non Exempt"
					? true
					: data.flsaStatus === "Exempt"
					? false
					: null,
			cgdg: data.cgdg || false,
			clip: data.clip || false,
			commissionedDate: data.commissionedDate
				? moment(data.commissionedDate, "MM/DD/YYYY").format(
						"YYYY-MM-DD"
				  )
				: null,
			trooperDate: data.trooperDate
				? moment(data.trooperDate, "MM/DD/YYYY").format("YYYY-MM-DD")
				: null,
			latestHireDate: data.latestHireDate
				? moment(data.latestHireDate, "MM/DD/YYYY").format("YYYY-MM-DD")
				: null,
			hireDate: data.hireDate
				? moment(data.hireDate, "MM/DD/YYYY").format("YYYY-MM-DD")
				: null,
			badgeNum: data.badgeNum.trim() || null,
			department: data.department.trim() || null,
			departmentDate: data.departmentDate
				? moment(data.departmentDate, "MM/DD/YYYY").format("YYYY-MM-DD")
				: null,
			section: data.section.trim() || null,
			sectionDate: data.sectionDate
				? moment(data.sectionDate, "MM/DD/YYYY").format("YYYY-MM-DD")
				: null,
			residentialCounty: data.residentialCounty || null,
			assignedCounty: data.assignedCounty || null,
			education: data.education.trim() || null,
			notes: data.notes.trim() || null,
			militaryNotes: data.militaryNotes.trim() || null,
			dropDate: data.dropDate
				? moment(data.dropDate, "MM/DD/YYYY").format("YYYY-MM-DD")
				: null,
			retireDate: data.retireDate
				? moment(data.retireDate, "MM/DD/YYYY").format("YYYY-MM-DD")
				: null,
			separationDate: data.separationDate
				? moment(data.separationDate, "MM/DD/YYYY").format("YYYY-MM-DD")
				: null,
			separationCode: data.separationCode.trim() || null,
			separationComments: data.separationComments.trim() || null,
			rehire:
				data.rehire === "OK to rehire"
					? 1
					: data.rehire === "Do not rehire"
					? 2
					: null,
		};

		if (oldEmployeeNum !== cleanedData.employeeNum) {
			const trainingParticipantQuery = {
				employeeNum: oldEmployeeNum,
				$limit: -1,
			};
			const trainingCertificationQuery = {
				employee: oldEmployeeNum,
				$limit: -1,
			};

			const trainingParticipantService = getService(
				serviceNames.perTraining
			);
			const trainingCertificationService = getService(
				serviceNames.perTrainingCertification
			);

			const [trainingParticipantResponse, trainingCertificationResponse] =
				await Promise.all([
					trainingParticipantService.find({
						query: trainingParticipantQuery,
					}),
					trainingCertificationService.find({
						query: trainingCertificationQuery,
					}),
				]);

			const bulkUpdate = async (records, updater, key) => {
				await Promise.all(
					records.map((record) =>
						dispatch(
							updater(
								record.id,
								{ [key]: cleanedData.employeeNum },
								false
							)
						)
					)
				);
			};

			if (trainingParticipantResponse.length > 0) {
				await bulkUpdate(
					trainingParticipantResponse,
					updateTrainingParticipant,
					"employeeNum"
				);
			}

			if (trainingCertificationResponse.length > 0) {
				await bulkUpdate(
					trainingCertificationResponse,
					updateTrainingCertification,
					"employee"
				);
			}
		}

		await dispatch(updateEmployee(data.id, cleanedData));
		history.push(`${PATH_EMPLOYEE}`);
	};

	if (!loaded) return null;

	const sectionProps = {
		control,
		errors,
		options,
		theme,
		watch,
	};

	const formSections2 = [
		separationReadPermission && {
			title: "Separation",
			icon: <NoMeetingRoom />,
			fields: separationFields,
			allExpanded,
			editPermission: !readOnly && separationEditPermission,
		},
	];

	const formSections3 = [
		educationReadPermission && {
			title: "Education",
			icon: <School />,
			fields: educationFields,
			allExpanded,
			editPermission: !readOnly && educationEditPermission,
		},
	];

	const formSections4 = [
		payrollReadPermission && {
			title: "Payroll",
			icon: <MonetizationOn />,
			fields: payrollFields,
			allExpanded,
			editPermission: !readOnly && payrollEditPermission,
		},
		medicalReadPermission && {
			title: "Medical Fields",
			icon: <MedicalServices />,
			fields: medicalFields,
			allExpanded,
			editPermission: !readOnly && medicalEditPermission,
		},
	];

	const handlePrintEmployeeProfile = async () => {
		const result = await printAMSReport(
			AMSReportID.Employees,
			id,
			userAgency,
			employeeModulePermissions,
			user?.Username
		);

		if (result) {
			setReportString(result?.base64 || "");
			setPrintReport(true);
		}
	};

	return (
		loaded && (
			<Paper
				elevation={3}
				sx={{
					padding: theme.spacing(3),
					margin: theme.spacing(1),
					backgroundColor: theme.palette.background.paper,
					overflow: "hidden",
				}}
			>
				<Box
					display="flex"
					justifyContent="space-between"
					alignItems="center"
					mb={1}
				>
					<Box display="flex" alignItems="center" mb={1}>
						<IconButton
							onClick={handleToggleAll}
							title={allExpanded ? "Collapse All" : "Expand All"}
							sx={{ mr: 1 }}
						>
							{allExpanded ? <ExpandLess /> : <ExpandMore />}
						</IconButton>
						{isLoadingPhotos ? (
							<CircularProgress size={24} sx={{ mr: 1, ml: 1 }} />
						) : thumbnailSrc ? (
							<Box mr={2}>
								<Box
									component="img"
									src={thumbnailSrc}
									alt="Thumbnail"
									sx={{
										width: 50,
										height: 50,
										borderRadius: "50%",
										objectFit: "cover",
										border: "2px solid #ccc",
									}}
								/>
							</Box>
						) : null}
						<Typography
							variant="h4"
							gutterBottom
							color={theme.palette.text.primary}
						>
							{`Employee Record of ${personName || ""}`}
						</Typography>
					</Box>

					<Box display="flex" gap={2}>
						{!isDisabledModule && (
							<>
								<PATPrintButton
									tooltipText="Print Employee Profile"
									color="primary"
									onClick={handlePrintEmployeeProfile}
								/>
								{!readOnly && (
									<PATSaveButton
										variant="contained"
										color="primary"
										onClick={handleSubmit(onSubmit)}
									/>
								)}
							</>
						)}
						<PATCloseButton
							tooltipText="Close"
							onClick={() => history.push(`${PATH_EMPLOYEE}`)}
						/>
					</Box>
				</Box>

				<Box sx={{ height: "70vh", overflowY: "auto", pr: 1 }}>
					<form>
						{generalInfoReadPermission && (
							<GeneralInfoSection
								control={control}
								errors={errors}
								icon={<Person />}
								options={options}
								generalInfoFields={generalInfoFields}
								theme={theme}
								title="General Info"
								watch={watch}
								allExpanded={allExpanded}
								defaultExpanded={true}
								setValue={setValue}
								formValues={getValues()}
								id={id}
								statusHighlight={statusHighlight}
								dateHighlight={dateHighlight}
								isLoadingPhotos={isLoadingPhotos}
								editPermission={
									!readOnly && generalInfoeEditPermission
								}
							/>
						)}
						{assignmentHistoryReadPermission && (
							<AssignmentHistoryTable
								personnelId={id}
								departmentOptions={departmentOptions}
								titlePositionOptions={titlePositionOptions}
								rankOptions={rankOptions}
								sectionOptions={sectionOptions}
								personnelAssignmentHistory={
									personnelAssignmentHistory
								}
								theme={theme}
								allExpanded={allExpanded}
								latestAssignment={latestAssignment}
								onAssignmentUpdate={setLatestAssignment}
								canAdd={assignmentHistoryAddPermission}
								canEdit={assignmentHistoryEditPermission}
								canDelete={assignmentHistoryDeletePermission}
								readOnly={readOnly}
							/>
						)}
						{formSections2.filter(Boolean).map((section) => (
							<FormSection
								key={section.title}
								{...section}
								{...sectionProps}
							/>
						))}

						{tagReadPermission && (
							<TagsTable
								id={id}
								tagTypeOptions={tagTypeOptions}
								personnelTags={personnelTags}
								theme={theme}
								allExpanded={allExpanded}
								perTagType={PerTagType.Employee}
								readOnly={readOnly}
							/>
						)}
						{formSections3.filter(Boolean).map((section) => (
							<FormSection
								key={section.title}
								{...section}
								{...sectionProps}
							/>
						))}
						{photoReadPermission && (
							<PhotosTable
								id={id}
								personnelPhotos={personnelPhotos}
								theme={theme}
								allExpanded={allExpanded}
								photoFor={PerPhotoType.Employee}
								readOnly={readOnly}
							/>
						)}
						{formSections4.filter(Boolean).map((section) => (
							<FormSection
								key={section.title}
								{...section}
								{...sectionProps}
							/>
						))}
						{emergencyReadPermission && (
							<EmergencyContactsTable
								personnelId={id}
								stateOptions={stateOptions}
								cityOptions={cityOptions}
								personnelEmergencyContacts={
									personnelEmergencyContacts
								}
								theme={theme}
								allExpanded={allExpanded}
								canAdd={emergencyAddPermission}
								canEdit={emergencyEditPermission}
								canDelete={emergencyDeletePermission}
								readOnly={readOnly}
							/>
						)}
						{noteReadPermission && (
							<PerNotesTable
								noteFor={perNotesOptions.Employees}
								entityId={id}
								perNotes={perNotes}
								theme={theme}
								allExpanded={allExpanded}
								readOnly={readOnly}
							/>
						)}
					</form>
				</Box>

				<PrintBase64Pdf base64={reportString} open={printReport} />
			</Paper>
		)
	);
};

export default EmployeeEditForm;
